import {
  Edit,
  Box,
  TextField,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormHelperText,
} from "@pankod/refine-mui";

import { Controller, useForm } from "@pankod/refine-react-hook-form";

import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { REST_PUBLIC_URI } from "environment";
import { AxiosInstance } from "axios";
import { axiosInstance } from "@pankod/refine-simple-rest";
import { useState } from "react";

const schema = Yup.object({
  bot_id: Yup.number().label("Bot Id").required(),
  name: Yup.string().label("Name").trim().required(),
  symbol: Yup.string().label("Symbol").trim().required(),
  symbol_display: Yup.string().label("Symbol Display").trim().required(),
  min_usd_start: Yup.number().label("Min Usd Start").required(),
  min_usd_stop: Yup.number().label("Min Usd Stop").required(),
  tag: Yup.mixed()
    .when({
      is: Array.isArray,
      then: () => Yup.array().of(Yup.string()),
      otherwise: () => Yup.string().transform((value) => value.split(",")),
    })
    .label("Tag")
    .required(),
  about: Yup.string().label("About").nullable(),
  timeframe: Yup.string().label("Select Timeframe").required(),
  leverage: Yup.number().label("Leverage").required(),
  bot_file_name: Yup.string().label("Bot File Name").required(),
  is_actived: Yup.boolean().label("Is Actived").required(),
  percent_win_rate: Yup.number()
    .label("Percent Win Rate")
    .nullable()
    .transform((val) => (val === Number(val) ? val : null)),
  total_percent_return: Yup.number()
    .label("Total Percent Winrate")
    .nullable()
    .transform((val) => (val === Number(val) ? val : null)),
  percent_max_draw_down: Yup.number()
    .label("Percent max Draw Down")
    .nullable()
    .transform((val) => (val === Number(val) ? val : null)),
  recovery_period: Yup.number()
    .integer()
    .label("Recovery Period")
    .nullable()
    .transform((val) => (val === Number(val) ? val : null)),
  trade_count: Yup.number()
    .label("Trade Count")
    .nullable()
    .transform((val) => (val === Number(val) ? val : null))
    .integer(),
  logo_url: Yup.string().label("Logo Url").nullable(),
  trend_type: Yup.string().label("Trend Type").nullable(),
  risk_type: Yup.string().label("Risk Type").nullable(),
  backtest_start_at: Yup.date()
    .label("Backtest Start At")
    .nullable()
    .transform((curr, orig) => (orig === "" ? null : curr)),
  backtest_end_at: Yup.date()
    .label("Backtest End At")
    .nullable()
    .transform((curr, orig) => (orig === "" ? null : curr)),
  file: Yup.mixed().label("File"),
});

interface formDataType {
  name: string;
  symbol: string;
  symbol_display: string;
  min_usd_start: number;
  min_usd_stop: number;
  tag: string | string[];
  about: string;
  timeframe: string;
  leverage: number;
  bot_file_name: string;
  percent_win_rate: number;
  total_percent_return: number;
  percent_max_draw_down: number;
  recovery_period: number;
  trade_count: number;
  logo_url: string;
  trend_type: string;
  risk_type: string;
  backtest_start_at: string;
  backtest_end_at: string;
}

interface data {
  url: string;
}

interface fileUpload {
  data: data;
}

const allowedExtension = ["image/jpeg", "image/jpg", "image/png"];

const upload = async (base64: File) => {
  const x = new FormData();

  x.append("file", base64);
  const httpClient: AxiosInstance = axiosInstance;
  const url = `${REST_PUBLIC_URI}/api/admin/v1/upload-file`;

  const data: fileUpload = await httpClient.post(url, x);

  return data;
};

export const BotEdit = () => {
  const onChangeHandler = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    try {
      setError(false);
      const target = event.target;
      const file: File = (target.files as FileList)[0];
      if (!allowedExtension.includes(file.type)) {
        setError(true);
        setHelperText("Only image is allowed");
        return;
      }
      const logo_url = await upload(file);
      setValue("logo_url", logo_url.data.url, { shouldValidate: true });
    } catch (error) {
      setHelperText(error as string);
      setError(true);
    }
  };

  const {
    saveButtonProps,
    register,
    setValue,
    control,
    formState: { errors },
  } = useForm<formDataType>({ resolver: yupResolver(schema) });

  const [error, setError] = useState<boolean>(false);
  const [helperText, setHelperText] = useState<string>("Only image is allowed");

  return (
    <Edit canDelete={false} saveButtonProps={saveButtonProps}>
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column" }}
        autoComplete="off"
      >
        <TextField
          {...register("bot_id")}
          required
          error={!!(errors as any)?.bot_id}
          helperText={(errors as any)?.bot_id?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="number"
          label="Bot_id"
          name="bot_id"
          disabled
        />
        <TextField
          {...register("name")}
          required
          error={!!(errors as any)?.name}
          helperText={(errors as any)?.name?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Name"
          name="name"
        />
        <TextField
          {...register("symbol")}
          required
          error={!!(errors as any)?.symbol}
          helperText={(errors as any)?.symbol?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Symbol"
          name="symbol"
        />
        <TextField
          {...register("symbol_display")}
          required
          error={!!(errors as any)?.symbol_display}
          helperText={(errors as any)?.symbol_display?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Symbol Display"
          name="symbol_display"
        />
        <TextField
          {...register("min_usd_start")}
          required
          error={!!(errors as any)?.min_usd_start}
          helperText={(errors as any)?.min_usd_start?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          label="Min_usd_start"
          name="min_usd_start"
        />
        <TextField
          {...register("min_usd_stop")}
          required
          error={!!(errors as any)?.min_usd_stop}
          helperText={(errors as any)?.min_usd_stop?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          label="Min_usd_stop"
          name="min_usd_stop"
        />
        <TextField
          {...register("about")}
          error={!!(errors as any)?.about}
          helperText={(errors as any)?.about?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          label="About"
          name="about"
        />
        <TextField
          {...register("bot_file_name")}
          required
          error={!!(errors as any)?.bot_file_name}
          helperText={(errors as any)?.bot_file_name?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          label="Bot_file_name"
          name="bot_file_name"
        />
        <TextField
          {...register("timeframe")}
          required
          error={!!(errors as any)?.timeframe}
          helperText={(errors as any)?.timeframe?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          label="Select Timeframe (1m, 5m, 15m, 30m, 1h, 2h, 4h, 6h, 12h, 1d)"
          name="timeframe"
        />
        <TextField
          {...register("leverage")}
          required
          error={!!(errors as any)?.leverage}
          helperText={(errors as any)?.tag?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Leverage"
          name="leverage"
        />
        <TextField
          {...register("tag")}
          required
          error={!!(errors as any)?.tag}
          helperText={(errors as any)?.tag?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Tag"
          name="tag"
        />
        <Controller
          control={control}
          name="show_explore"
          render={({ field: { onChange, value } }) => (
            <FormControl required error={!!(errors as any)?.show_explore}>
              <FormLabel>Show Explore</FormLabel>
              <RadioGroup
                row
                onChange={(_, value) => onChange(value === "true")}
                value={value ? "true" : "false"}
              >
                <FormControlLabel
                  control={<Radio />}
                  label="Yes"
                  value={true} // Change these to string to match the expected value format
                />
                <FormControlLabel
                  control={<Radio />}
                  label="No"
                  value={false} // Change these to string to match the expected value format
                />
              </RadioGroup>
              <FormHelperText>{(errors as any)?.show_explore?.message}</FormHelperText>
            </FormControl>
          )}
        />
        <Controller
          control={control}
          name="is_actived"
          render={({ field: { onChange, value } }) => (
            <FormControl required error={!!(errors as any)?.is_actived}>
              <FormLabel>Active</FormLabel>
              <RadioGroup
                row
                onChange={(_, value) => onChange(value === "true")}
                value={value ? "true" : "false"} // Ensure the value is a string matching the FormControlLabel values
              >
                <FormControlLabel
                  control={<Radio />}
                  label="Yes"
                  value={true}
                />
                <FormControlLabel
                  control={<Radio />}
                  label="No"
                  value={false}
                />
              </RadioGroup>
              <FormHelperText>
                {(errors as any)?.is_actived?.message}
              </FormHelperText>
            </FormControl>
          )}
        />
        {/* <Controller
          control={control}
          name="Is Exclusive"
          render={({ field: { onChange, value } }) => (
            <FormControl required error={!!(errors as any)?.check_affiliate}>
              <FormLabel>Check Affiliate</FormLabel>
              <RadioGroup
                row
                onChange={(_, value) => onChange(value === "true")}
                value={value}
              >
                <FormControlLabel
                  control={<Radio />}
                  label="True"
                  value={true}
                />
                <FormControlLabel
                  control={<Radio />}
                  label="False"
                  value={false}
                />
              </RadioGroup>
              <FormHelperText>
                {(errors as any)?.check_affiliate?.message}
              </FormHelperText>
            </FormControl>
          )}
        /> */}
         <TextField
          {...register("trend_type")}
          error={!!(errors as any)?.trend_type}
          helperText={(errors as any)?.trend_type?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Trend Type "
          name="trend_type"
        />
        <TextField
          {...register("risk_type")}
          error={!!(errors as any)?.risk_type}
          helperText={(errors as any)?.risk_type?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Risk Type"
          name="risk_type"
        />
        {/* <TextField
          {...register("percent_win_rate")}
          error={!!(errors as any)?.percent_win_rate}
          helperText={(errors as any)?.percent_win_rate?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Percent Win Rate"
          name="percent_win_rate"
        />
        <TextField
          {...register("total_percent_return")}
          error={!!(errors as any)?.total_percent_return}
          helperText={(errors as any)?.total_percent_return?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Total Percent Return"
          name="total_percent_return"
        />
        <TextField
          {...register("percent_max_draw_down")}
          error={!!(errors as any)?.percent_max_draw_down}
          helperText={(errors as any)?.percent_max_draw_down?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Percent Max Draw Down"
          name="percent_max_draw_down"
        />
        <TextField
          {...register("recovery_period")}
          error={!!(errors as any)?.recovery_period}
          helperText={(errors as any)?.recovery_period?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Recovery period"
          name="recovery_period"
        />
        <TextField
          {...register("trade_count")}
          error={!!(errors as any)?.trade_count}
          helperText={(errors as any)?.trade_count?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Trade Count"
          name="trade_count"
        />
        <TextField
          {...register("backtest_start_at")}
          error={!!(errors as any)?.backtest_start_at}
          helperText={(errors as any)?.backtest_start_at?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="datetime-local"
          label="Backtest Start At"
          name="backtest_start_at"
        />
        <TextField
          {...register("backtest_end_at")}
          error={!!(errors as any)?.backtest_end_at}
          helperText={(errors as any)?.backtest_end_at?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="datetime-local"
          label="Backtest End At"
          name="backtest_end_at"
        /> */}
        <TextField
          onChange={onChangeHandler}
          error={error}
          helperText={error ? helperText : null}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="file"
          label="File"
          name="file"
        />
        <TextField
          style={{ display: "none" }}
          {...register("logo_url")}
          disabled
          error={!!(errors as any)?.logo_url}
          helperText={(errors as any)?.logo_url?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Logo Url"
          name="logo_url"
        />
      </Box>
    </Edit>
  );
};
